import {LitElement, css, html} from 'lit';
import {property, customElement} from 'lit/decorators.js';

@customElement('voyage-time-scale-control')
export class VoyageTimeScaleControl extends LitElement
{
    @property({type: Number})
    get selection()
    {
        return this._selection;
    }

    set selection(value)
    {
        this._selection = value;
        this._highlightOffset = this._pointOffsets[value];
    }

    static get styles()
    {
        return [
            css`
            :host {
			}
			
            .container {
                display: flex;
                font-family: MaerskText;
                font-size: 12px;
                align-items: center;
            }

            .label {
                margin-right: 5px;
                margin-left: 5px;
            }

            .slider {
                height: 36px;
                position: relative;
                display: flex;
                align-items: center;
                cursor: pointer;
            }

            .line {
                position: absolute;
                margin: auto;
                height: 1px;
                width: calc(100% - 5px);
                background-color: var(--neutrals-grey-06, grey);
            }

            .dot {
                margin-left: 20px;
                height: 5px;
                width: 5px;
                border-radius: 2.5px;
                background-color: var(--neutrals-grey-06, grey);
            }

            .dot:first-child {
                margin-left: 0px;
            }

            #highlight {
                position: absolute;
                margin-top: auto;
                margin-bottom: auto;
                background-color: var(--primary-blue-05, blue);
                width: 11px;
                height: 11px;
                border-radius: 5.5px;
            }

            `
        ];
    }

    render()
    {
        return html`
        <div class="container">
            <div class="label">+</div>
            <div class="slider" @click=${this._onClick}>
                <div class="dot"></div>
                <div class="dot"></div>
                <div class="dot"></div>
                <div class="line"></div>
                <div id="highlight" style="left:${this._highlightOffset}px"
                    @pointerdown=${this._onPointerDown} @pointerup=${this._onPointerUp} @pointermove=${this._onPointerMove}></div>
            </div>
            <div class="label">-</div>
        </div>
        `;
    }

    firstUpdated()
    {
        this._slider = this.shadowRoot!.querySelector(".slider");
    }

    @property({attribute: false}) private _highlightOffset: number = -3;
    private _selection: number = 0;
    private _dragging: boolean = false;
    private _startX: number = 0;
    private _startHighlightOffset: number = 0;
    private _pointOffsets: number[] = [-3, 22, 47];
    private _slider: HTMLElement|null = null;

    private _onPointerDown(evt: PointerEvent)
    {
        this._dragging = true;
        this._startX = evt.clientX;
        this._startHighlightOffset = this._highlightOffset;
        (<HTMLElement>evt.target).setPointerCapture(evt.pointerId);
    }

    private _onPointerUp(evt: PointerEvent)
    {
        if (this._dragging)
        {
            this._dragging = false;
            const delta = evt.clientX - this._startX;
            this._setHighlightOffset(this._startHighlightOffset + delta, true);
            (<HTMLElement>evt.target).releasePointerCapture(evt.pointerId);
        }
    }

    private _onPointerMove(evt: PointerEvent)
    {
        if (this._dragging)
        {
            const delta = evt.clientX - this._startX;
            this._setHighlightOffset(this._startHighlightOffset + delta);
        }
    }

    private _onClick(evt: MouseEvent)
    {
        const rect = this._slider!.getBoundingClientRect();
        this._setHighlightOffset(evt.clientX - rect.left - 5.5, true);
    }

    private _setHighlightOffset(offset: number, snapToPoint?: boolean)
    {
        if (offset < 0)
        {
            offset = 0;
        }
        if (offset > this._pointOffsets[this._pointOffsets.length-1])
        {
            offset = this._pointOffsets[this._pointOffsets.length-1];
        }

        if (snapToPoint)
        {
            for (let i = 0; i < this._pointOffsets.length; i++)
            {
                if (offset <= this._pointOffsets[i])
                {
                    if (i > 0 && offset - this._pointOffsets[i-1] < this._pointOffsets[i] - offset)
                    {
                        offset = this._pointOffsets[i-1];
                        this._selection = i-1;
                    }
                    else
                    {
                        offset = this._pointOffsets[i];
                        this._selection = i;
                    }
                    break;
                }
            }
            this.dispatchEvent(new CustomEvent("selection-changed", { detail: this._selection } ));
        }

        this._highlightOffset = offset;
    }
};
